/**
 * Grimoire Noir, the app's direction (the GM's choice of 2026-09-28,
 * docs/PLAN.md section 2). Core tokens from 07-design-mockup-build-notes.md
 * section 1 (column C, with the critic's danger); type hues from the critic's
 * replacement map, column C; `spell` from amendments 2.1 section 7.
 *
 * Decided by the GM with the direction, and logged in docs/reports/p0-9.md
 * (Patch 1): AI proposals and actions use the violet accent, so `ai` equals
 * `accent` (it was a separate azure); the user type Deity has a pale
 * silver-white hue of its own (a cool pewter in light mode, where a pale
 * color cannot reach 4.5 on the surfaces).
 */
import { PALETTE } from '../palette.js';
export const noir = {
    id: 'noir',
    name: 'Grimoire Noir',
    mood: 'Jewel tones on near-black plum: a premium game companion rather than a wiki, with violet kept for what you can act on.',
    modes: {
        dark: {
            core: {
                bg: '#14111A',
                surface: '#1D1926',
                surfaceRaised: '#272233',
                border: '#332C42',
                borderStrong: '#453E55',
                controlBorder: '#9D95B1',
                text: '#EDEAF5',
                textMuted: '#9D95B1',
                accent: '#A78BFA',
                accentContrast: '#14111A',
                danger: '#E07789',
                success: '#68D4A6',
                ai: '#A78BFA',
            },
            types: {
                pc: '#91CFFA',
                npc: '#50EFC6',
                creature: '#F9A85C',
                encounter: '#F67A6B',
                location: '#A5C793',
                faction: '#9CB0FC',
                item: '#D3E58F',
                lore: '#BE91CC',
                arc: '#D28F9D',
                quest: '#FED60E',
                script: '#DEA5C7',
                session: '#9CE9FD',
                spell: '#E4960D',
                note: '#9D95B1',
                deity: '#FCFDFE',
            },
            palette: PALETTE.dark,
            elevation: { shadow: 'none', border: 'border' },
        },
        light: {
            core: {
                bg: '#F2EEF7',
                surface: '#FAF8FC',
                surfaceRaised: '#FFFFFF',
                border: '#DCD5E8',
                borderStrong: '#C9C2D4',
                controlBorder: '#625971',
                text: '#1D1926',
                textMuted: '#625971',
                accent: '#6D3FC4',
                accentContrast: '#FFFFFF',
                danger: '#B1262C',
                success: '#0E6B57',
                ai: '#6D3FC4',
            },
            types: {
                pc: '#0B3E62',
                npc: '#025D4D',
                creature: '#773F13',
                encounter: '#741C28',
                location: '#496738',
                faction: '#323CAF',
                item: '#323C00',
                lore: '#54308A',
                arc: '#5C163B',
                quest: '#845100',
                script: '#750B72',
                session: '#1F6580',
                spell: '#3D290F',
                note: '#625971',
                deity: '#3B4B5C',
            },
            palette: PALETTE.light,
            elevation: {
                shadow: '0 1px 2px rgb(29 25 38 / 0.08), 0 6px 16px rgb(29 25 38 / 0.1)',
                border: 'borderStrong',
            },
        },
    },
    userTypeHues: ['deity'],
    typography: {
        display: {
            family: '"Cormorant Garamond", "Cormorant", Garamond, Georgia, serif',
            weight: 600,
            variation: 'normal',
        },
        heading: {
            family: '"Alegreya", "Iowan Old Style", Georgia, serif',
            weight: 700,
            variation: 'normal',
        },
        body: {
            family: '"Alegreya", "Iowan Old Style", Georgia, serif',
            weight: 400,
            variation: 'normal',
        },
        label: {
            family: '"Geist", "Segoe UI", system-ui, sans-serif',
            weight: 500,
            variation: 'normal',
        },
        ui: {
            family: '"Geist", "Segoe UI", system-ui, sans-serif',
            weight: 400,
            variation: 'normal',
        },
        mono: {
            family: '"Geist Mono", "SFMono-Regular", Menlo, Consolas, monospace',
            weight: 400,
            variation: 'normal',
        },
        size: { title: 44, heading: 22, body: 17, ui: 14, label: 12 },
        bodyWeightDark: 400,
        labelCase: 'uppercase',
        labelTracking: '0.06em',
    },
    radii: { chip: 999, card: 10, control: 8 },
    ornament: {
        dropCapInRead: false,
        titleRule: false,
        scaleBar: false,
        divider: 'diamond',
        glow: true,
        statBlockEdge: 'muted',
        secretBorder: 'accent',
        gmNotePanel: true,
    },
    fixes: [
        'Cormorant Garamond only at 32 px or larger; scene headings and smaller headings in Alegreya',
        'Pill chips instead of gem shapes (no clip-path, so focus rings and forced colors survive)',
        'No hue edge on stat blocks: the top edge uses text-muted',
        'AI proposals and actions use the violet accent, marked by the dashed Draft outline and label',
        'The Deity type has its own glyph and a pale silver-white hue',
        'Danger replaced with #E07789 / #B1262C',
    ],
};
//# sourceMappingURL=noir.js.map